/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'react-resource-router';

import { IconButton } from '@atlaskit/button/new';
import BugIcon from '@atlaskit/icon/core/bug';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { usePanelContext } from '../../../hooks/use-panel-context';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { CLI_ROUTE_NAME } from '../../../routes';
import { SiteSwitcher } from '../SiteSwitcher';

import { BrandHomeLink } from './BrandHomeLink';
import { NAV_BAR_HEIGHT_PX } from './constants';
import { FeedbackControl } from './FeedbackControl';
import { messages } from './messages';
import { NavigationBarOld } from './NavBarOld';
import { NavButtons } from './NavButtons';
import { UserAvatarDropdown } from './UserAvatarDropdown';

export { NAV_BAR_HEIGHT_PX } from './constants';

const styles = cssMap({
	navBar: {
		width: '100%',
		backgroundColor: '#111213',
		position: 'fixed',
		top: 0,
		zIndex: 10,
	},
	navBarContent: {
		width: '100%',
		maxWidth: '1264px',
		height: `${NAV_BAR_HEIGHT_PX}px`,
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		paddingInline: token('space.400'),
		marginInline: 'auto',
	},
	rightControls: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.200'),
		justifySelf: 'end',
	},
	loginButton: {
		display: 'inline-flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '10.5px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '6.5px',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		cursor: 'pointer',
		transition: 'all 150ms ease-out',
		zIndex: 1,
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const navBarContentStyles = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const leftNavStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.400'),
});

export const NavigationBarNew = (): JSX.Element => {
	const { identity: user, loading, error, attemptUserLogin } = useAtlassianUserContext();
	const { formatMessage } = useIntl();
	const { togglePanel, isOpen } = usePanelContext();
	const { fireEvent } = useAnalyticsEvents();
	const [{ route }] = useRouter();
	// `/cli` is a dedicated install experience: the site switcher and the nav
	// login button do not apply there. The nav buttons do still render, so
	// visitors can get back to the home page sections.
	const isCliPage = route?.name === CLI_ROUTE_NAME;

	const handleLoginClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'navBar' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	const isLoggedIn = !loading && !error && !!user.email;
	const brandLabel = formatMessage(messages.teamworkGraphBrand);

	return (
		<UFOSegment name="twg-microsite.nav-bar">
			<div css={styles.navBar}>
				<Box xcss={styles.navBarContent}>
					<div css={navBarContentStyles}>
						<div css={leftNavStyles}>
							<BrandHomeLink />
							{!loading && <NavButtons ariaLabel={brandLabel} isLoggedIn={isLoggedIn} />}
						</div>

						<div css={styles.rightControls}>
							{/* TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback */}
							{loading && (
								// `data-vc-ignore-if-no-layout-shift="true"` excludes the
								// auth-loading spinner from React UFO's VC tracking. The
								// `@atlaskit/spinner` SVG runs an infinite CSS animation in
								// a fixed-size box (no layout shift) so VC can safely
								// ignore its mutations — otherwise the spinner kicks the
								// VC quiet window for the full duration of auth resolution
								// and prevents the page-load interaction from sealing.
								<span data-vc-ignore-if-no-layout-shift="true">
									<Spinner size="small" interactionName="twg-microsite.nav-bar.user-loading" />
								</span>
							)}

							{!loading && <FeedbackControl />}

							{error && !isCliPage && (
								<button
									css={styles.loginButton}
									onClick={handleLoginClick}
									aria-label={formatMessage(messages.loginAriaLabel)}
								>
									<FormattedMessage {...messages.loginButton} />
								</button>
							)}

							{!loading && !error && user && !isCliPage && <SiteSwitcher />}
							{!loading && !error && user && <UserAvatarDropdown user={user} />}

							{fg('twg_microsite_debug_panel') && (
								<IconButton
									icon={BugIcon}
									label="Debug Menu Toggle"
									appearance={isOpen ? 'discovery' : 'default'}
									onClick={togglePanel}
								/>
							)}
						</div>
					</div>
				</Box>
			</div>
		</UFOSegment>
	);
};

/**
 * Picks the nav bar variant for the current rollout state. The gated variant
 * above is route-aware (brand link home, a CLI item that routes rather than
 * scrolls, and controls that hide on `/cli`); the old one is untouched.
 *
 * Gate cleanup: delete `NavBarOld`, drop its import and this ternary, and
 * rename `NavigationBarNew` to `NavigationBar`. `NavBarOld` also owns the only
 * `NavButtons` stories, which `__tests__/vr-tests/NavBar.vr.tsx` imports, so
 * that VR file needs updating in the same change.
 */
export const NavigationBar = (): JSX.Element =>
	fg('twg-microsite-enable-standalone-cli-page') ? <NavigationBarNew /> : <NavigationBarOld />;
